import { ApiError, apiErrorCode } from "@/lib/api";
import type { AuthedRequester } from "@/lib/brokerAuth";
import { duplicateEmailMessage } from "@/lib/bondRosterChecks";

// BRK-S182 — the GENERAL INDEMNITY AGREEMENT, as a broker sees it.
//
// Every listed owner and every listed owner's spouse signs the GIA before any
// bond is issued. PMS sends it through DocuSign (PMS-S483), and this portal
// NEVER talks to DocuSign: the three own-scoped BFF routes below (PMS-S484) are
// its only path, and the order read carries a small `gia` summary beside them.
//
// ── WHAT A BROKER MAY SEE, AND NOTHING MORE ─────────────────────────────────
//
// The status, the signers' names and the emails the broker typed. No document
// id, no envelope id, no DocuSign status text and no surety-side recipient —
// PMS publishes none of them on a broker read, and nothing here declares a home
// for one (`test/lib/giaDiscretion.test.ts` sweeps `src/` for it). The executed
// PDF is filed on the ACCOUNT as a `gia` document that is not broker-visible.
//
// ── PINNED (PMS-S483 / PMS-S484, adopted ahead of the pin and re-pinned) ─────
//
// The order read's `gia` block is always emitted and is `null` unless the
// order is submitted, not referred, and its pre-approval is absent or approved
// (an approved standalone pre-approval counts). The reader still takes an
// absent key as `null`, which costs nothing. The status vocabulary is CLOSED,
// and a member this build does not know renders a neutral "Status unavailable"
// with nothing actionable, never a blank chip. The routes and shapes are held
// to the pinned contract by `test/lib/giaContract.test.ts`.

const BASE = "/api/broker/bond/submissions";

/** The GIA status vocabulary, closed (PMS-S483). */
export const GIA_STATUSES = [
  "not_required",
  "not_sent",
  "sent",
  "signed",
  "declined",
  "voided",
] as const;
export type GiaStatus = (typeof GIA_STATUSES)[number];

/** Why a GIA is required on this order. `null` when it is not required. */
export const GIA_REQUIRED_REASONS = [
  "first_gia",
  "indemnitors_changed",
] as const;
export type GiaRequiredReason = (typeof GIA_REQUIRED_REASONS)[number];

/** Why a person signs — as an owner, or as a listed owner's spouse. */
export const GIA_SIGNER_BASES = ["owner", "spouse_of"] as const;
export type GiaSignerBasis = (typeof GIA_SIGNER_BASES)[number];

/**
 * The order read's `gia` block (PMS-S483).
 *
 * `signed` is what makes the order READY, and that is a derived fact PMS
 * publishes here rather than a workflow status. `not_required` is derived too:
 * a GIA is already on file and the indemnitors have not changed.
 */
export interface BondGiaSummary {
  status: GiaStatus;
  requiredReason: GiaRequiredReason | null;
  sentAt: string | null;
  signedAt: string | null;
}

/** The owner a spouse's seat derives from. `name` is null when the roster
 *  names nobody for that owner. */
export interface GiaSpouseOf {
  ownerId: string;
  name: string | null;
}

/** One person who signs the GIA (PMS-S484). Only LISTED seats are returned. */
export interface GiaSigner {
  /** The person's OWN roster id — the spouse's own on a spouse seat — which
   *  is what `PUT …/gia/signers` addresses. */
  ownerId: string;
  basis: GiaSignerBasis;
  name: string;
  /** Null when nobody has supplied one yet — the broker fills it in. */
  email: string | null;
  /** The owner a SPOUSE signs beside; null on an owner's own seat. */
  spouseOf: GiaSpouseOf | null;
}

/** `GET …/gia` — the signers and where the GIA stands (PMS-S484). */
export interface BondGia extends BondGiaSummary {
  signers: GiaSigner[];
}

/** One confirmed email, as `PUT …/gia/signers` takes it. */
export interface GiaSignerEmail {
  ownerId: string;
  email: string;
}

/** A status this build knows, or null for one it does not (or none at all). */
export function readGiaStatus(raw: unknown): GiaStatus | null {
  if (typeof raw !== "string") return null;
  return GIA_STATUSES.find((s) => s === raw) ?? null;
}

const STATUS_LABELS: Record<GiaStatus, string> = {
  not_required: "Not required",
  not_sent: "Not sent",
  sent: "Sent",
  signed: "Signed",
  declined: "Declined — contact your underwriter",
  voided: "Voided",
};

/** The chip's words. An unrecognised status is said to be unavailable —
 *  never a blank, and never a guess at the nearest known one. */
export function giaStatusLabel(status: GiaStatus | null): string {
  return status === null ? "Status unavailable" : STATUS_LABELS[status];
}

/**
 * Whether an order gets a GIA section at all.
 *
 * A SUBMITTED order that is not referred (the approved outcome), carrying a
 * `gia` block whose status is not `not_required`. PMS publishes the block as
 * `null` on any order that is not approved — including a pre-approval whose
 * decision is not `approved` — so the block's presence is the approval test
 * and this build does not re-derive it. A draft, a referred or
 * cancelled order, an absent or null block, and a GIA already on file with an
 * unchanged roster all render nothing. An UNKNOWN status still renders — as
 * "Status unavailable" — because hiding it would be this build deciding the
 * GIA does not matter.
 */
export function showsGiaSection(order: {
  status: string;
  referred: boolean;
  gia?: { status: unknown } | null;
}): boolean {
  if (order.status !== "submitted" || order.referred) return false;
  if (!order.gia) return false;
  return readGiaStatus(order.gia.status) !== "not_required";
}

/** Emails are the broker's to confirm only before the GIA goes out. */
export function giaIsEditable(status: GiaStatus | null): boolean {
  return status === "not_sent";
}

/**
 * The seat's basis, in words: "Owner", or "Spouse of {owner name}" off the
 * contract's `spouseOf`. A spouse whose owner is unnamed reads plain "Spouse" —
 * the name is never guessed from a neighbouring row.
 */
export function giaBasisLabel(signer: GiaSigner): string {
  if (signer.basis === "owner") return "Owner";
  const named = signer.spouseOf?.name;
  return named ? `Spouse of ${named}` : "Spouse";
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const GIA_EMAIL_MISSING =
  "Enter an email address — every signer needs one.";
export const GIA_EMAIL_INVALID = "Enter a valid email address.";

/**
 * The client half of the server's three refusals, keyed by ownerId: every
 * signer has an email, each one is shaped like an email, and no two signers
 * share one (case-insensitively). The server is the authority; this saves the
 * round trip and puts each reason on the row it is about.
 */
export function validateGiaEmails(
  emails: readonly GiaSignerEmail[],
): Record<string, string> {
  const errors: Record<string, string> = {};
  const byAddress = new Map<string, string[]>();
  for (const { ownerId, email } of emails) {
    const text = email.trim();
    if (text === "") {
      errors[ownerId] = GIA_EMAIL_MISSING;
      continue;
    }
    if (!EMAIL_SHAPE.test(text)) {
      errors[ownerId] = GIA_EMAIL_INVALID;
      continue;
    }
    const key = text.toLowerCase();
    byAddress.set(key, [...(byAddress.get(key) ?? []), ownerId]);
  }
  for (const [address, owners] of byAddress) {
    if (owners.length < 2) continue;
    // EVERY row that shares it: three people on one address is one mistake,
    // and naming two of them leaves a send that fails again.
    for (const ownerId of owners) {
      errors[ownerId] = duplicateEmailMessage(address);
    }
  }
  return errors;
}

/**
 * The refusals that name SIGNERS (400), each carrying
 * `details.fields: [{ownerId, reason}]` where `reason` is a CODE —
 * `missing | invalid | duplicate | unknown` — rather than a sentence.
 */
export const GIA_SIGNER_ROW_CODES = [
  "GIA_SIGNER_EMAIL_DUPLICATE",
  "GIA_SIGNER_EMAIL_INVALID",
  "GIA_SIGNER_EMAIL_MISSING",
  "GIA_SIGNER_UNKNOWN",
] as const;

export const GIA_EMAIL_DUPLICATE =
  "This email is already used by someone else on this request. Each person needs their own email address.";
export const GIA_SIGNER_UNKNOWN =
  "This person is no longer on the request. Reload the page to see who signs.";

/** The sentence for each `details.fields[].reason` code. */
const ROW_REASON: Readonly<Record<string, string>> = {
  missing: GIA_EMAIL_MISSING,
  invalid: GIA_EMAIL_INVALID,
  duplicate: GIA_EMAIL_DUPLICATE,
  unknown: GIA_SIGNER_UNKNOWN,
};

/** The sentence when the row's reason code is absent or unrecognised. */
const CODE_REASON: Record<(typeof GIA_SIGNER_ROW_CODES)[number], string> = {
  GIA_SIGNER_EMAIL_DUPLICATE: GIA_EMAIL_DUPLICATE,
  GIA_SIGNER_EMAIL_INVALID: GIA_EMAIL_INVALID,
  GIA_SIGNER_EMAIL_MISSING: GIA_EMAIL_MISSING,
  GIA_SIGNER_UNKNOWN: GIA_SIGNER_UNKNOWN,
};

/**
 * Refusals about the GIA itself that the broker cannot fix from this screen —
 * the order's values are missing something the agreement needs
 * (`GIA_VALUES_MISSING`), there are more signers than the template has
 * seats for (`GIA_SIGNER_SLOTS_EXCEEDED`), or a married owner's template has
 * no spouse seat (`GIA_SPOUSE_SLOT_MISSING`, BRK-S189 — its `details.slot`
 * names the role and is never shown). Said plainly, with who to ask, rather
 * than in the server's words.
 */
export const GIA_CONTACT_UNDERWRITER =
  "We can’t send this GIA from here. Please contact your underwriter.";
const CONTACT_UNDERWRITER_CODES = [
  "GIA_VALUES_MISSING",
  "GIA_SIGNER_SLOTS_EXCEEDED",
  "GIA_SPOUSE_SLOT_MISSING",
];

export interface GiaServerErrors {
  /** A reason per signer row, keyed by ownerId. */
  byOwner: Record<string, string>;
  /** The line beside the button — set when nothing could go on a row. */
  summary: string | null;
}

/**
 * A refused save or send, placed on the rows it names.
 *
 * The four signer codes land on their rows, each reason code turned into a
 * sentence. A field naming a signer this section is not drawing, or a refusal
 * that names none (the 409s), becomes the summary instead — a reason on the
 * wrong row is worse than one beside the button.
 */
export function giaServerErrors(
  err: unknown,
  ownerIds: readonly string[],
): GiaServerErrors {
  const fallback =
    err instanceof ApiError
      ? err.message
      : "We couldn’t send the GIA. Please try again.";
  const code = apiErrorCode(err);
  if (code !== null && CONTACT_UNDERWRITER_CODES.includes(code)) {
    return { byOwner: {}, summary: GIA_CONTACT_UNDERWRITER };
  }
  const known = GIA_SIGNER_ROW_CODES.find((c) => c === code);
  if (!known || !(err instanceof ApiError)) {
    return { byOwner: {}, summary: fallback };
  }
  const details = (err.body as { details?: { fields?: unknown } } | null)
    ?.details;
  const fields = Array.isArray(details?.fields) ? details.fields : [];
  const drawn = new Set(ownerIds);
  const byOwner: Record<string, string> = {};
  for (const entry of fields) {
    if (typeof entry !== "object" || entry === null) continue;
    const { ownerId, reason } = entry as {
      ownerId?: unknown;
      reason?: unknown;
    };
    if (typeof ownerId !== "string" || !drawn.has(ownerId)) continue;
    byOwner[ownerId] =
      (typeof reason === "string" ? ROW_REASON[reason] : undefined) ??
      CODE_REASON[known];
  }
  return Object.keys(byOwner).length > 0
    ? { byOwner, summary: null }
    : { byOwner, summary: fallback };
}

const giaPath = (id: string) => `${BASE}/${encodeURIComponent(id)}/gia`;

/** GET …/submissions/{id}/gia — the signers and the status. Own-scoped. */
export function getBondGia(
  request: AuthedRequester,
  id: string,
): Promise<BondGia> {
  return request<BondGia>(giaPath(id));
}

/**
 * PUT …/submissions/{id}/gia/signers — store the confirmed emails. Answers the
 * same read as the GET; the section does not move its chip on it.
 *
 * `pushToAccount` is the wizard's choice, asked again here (PMS-S484; the
 * contract defaults it to true): checked, the confirmed emails also update the account's people;
 * unchecked, they stay with this GIA. No push outcome is reported back to the
 * broker — the notice the wizard used to show was retired in BRK-S181
 * revision 8, and the same rule holds here.
 */
export function saveGiaSigners(
  request: AuthedRequester,
  id: string,
  signers: readonly GiaSignerEmail[],
  pushToAccount: boolean,
): Promise<BondGia> {
  return request<BondGia>(`${giaPath(id)}/signers`, {
    method: "PUT",
    body: { signers, pushToAccount },
  });
}

/** POST …/submissions/{id}/gia/send — send for signature. Idempotent: a
 *  repeat while `sent` or `signed` answers 200 with the same read. */
export function sendBondGia(
  request: AuthedRequester,
  id: string,
): Promise<BondGia> {
  return request<BondGia>(`${giaPath(id)}/send`, { method: "POST" });
}
